import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Settings, Bell, Mail, Trophy, Target, Users, Megaphone, Crown,
  Clock, Loader2, Zap, BarChart3, Moon, TrendingUp, Sparkles, Eye, EyeOff, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ToggleRow = ({ icon: Icon, label, description, checked, onChange, color = 'emerald', testId }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0">
    <div className="flex items-center gap-3 flex-1 min-w-0">
      {Icon && <Icon className={`h-4 w-4 text-${color}-400 flex-shrink-0`} />}
      <div className="min-w-0">
        <Label className="text-white text-sm font-medium">{label}</Label>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

const SettingsPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inAppPrefs, setInAppPrefs] = useState(null);
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [portfolioPublic, setPortfolioPublic] = useState(true);

  const fetchPreferences = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInAppPrefs(data.in_app);
        setEmailPrefs(data.email);
      }
      // Fetch portfolio privacy setting
      if (user?.id) {
        const profileRes = await fetch(`${API_URL}/api/profile/${user.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (profileRes.ok) {
          const profileData = await profileRes.json();
          setPortfolioPublic(profileData.portfolio_public ?? true);
        }
      }
    } catch (err) {
      toast.error('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  }, [token, user?.id]);

  useEffect(() => { fetchPreferences(); }, [fetchPreferences]);

  const togglePortfolioPrivacy = async (isPublic) => {
    setPortfolioPublic(isPublic);
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/profile/privacy`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ portfolio_public: isPublic })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed');
      }
      toast.success(isPublic ? 'Portfolio is now public' : 'Portfolio is now private');
    } catch (err) {
      setPortfolioPublic(!isPublic);
      toast.error(err.message || 'Failed to update privacy setting');
    } finally {
      setSaving(false);
    }
  };

  const updateInApp = async (key, value) => {
    setInAppPrefs(prev => ({ ...prev, [key]: value }));
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: value })
      });
      if (!res.ok) throw new Error();
      toast.success('Preference updated');
    } catch {
      setInAppPrefs(prev => ({ ...prev, [key]: !value }));
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  const updateEmail = async (key, value) => {
    const updated = { ...emailPrefs, [key]: value };
    setEmailPrefs(updated);
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences/email`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updated)
      });
      if (!res.ok) throw new Error();
      toast.success('Email preference updated');
    } catch {
      setEmailPrefs(prev => ({ ...prev, [key]: !value }));
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  const updateEmailFrequency = async (freq) => {
    setInAppPrefs(prev => ({ ...prev, email_frequency: freq }));
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_frequency: freq })
      });
      if (!res.ok) throw new Error();
      toast.success('Email frequency updated');
    } catch {
      toast.error('Failed to update frequency');
    } finally {
      setSaving(false);
    }
  };

  const updateQuietHours = async (field, value) => {
    setInAppPrefs(prev => ({ ...prev, [field]: value }));
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value })
      });
      if (!res.ok) throw new Error();
    } catch {
      toast.error('Failed to update quiet hours');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="settings-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Settings className="h-7 w-7 text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Settings</h1>
            <p className="text-sm text-slate-400">{user?.username} &middot; {user?.email}</p>
          </div>
          {saving && <Loader2 className="h-4 w-4 animate-spin text-emerald-400 ml-auto" />}
        </div>

        {/* In-App Notifications */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <Bell className="h-5 w-5 text-blue-400" /> In-App Notifications
                </CardTitle>
                <CardDescription className="mt-1">Control which notifications appear in your notification bell</CardDescription>
              </div>
              <Switch
                checked={inAppPrefs?.in_app_enabled ?? true}
                onCheckedChange={(v) => updateInApp('in_app_enabled', v)}
                data-testid="toggle-in-app-master"
              />
            </div>
          </CardHeader>
          {inAppPrefs?.in_app_enabled && (
            <CardContent className="pt-0">
              <div className="space-y-0">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 mt-2">Categories</p>
                <ToggleRow
                  icon={Trophy} label="League Notifications"
                  description="Rank changes, season updates, league invites"
                  checked={inAppPrefs?.league_notifications ?? true}
                  onChange={(v) => updateInApp('league_notifications', v)}
                  color="amber" testId="toggle-league"
                />
                <ToggleRow
                  icon={Target} label="Achievement Notifications"
                  description="Badges earned, milestones reached"
                  checked={inAppPrefs?.achievement_notifications ?? true}
                  onChange={(v) => updateInApp('achievement_notifications', v)}
                  color="purple" testId="toggle-achievement"
                />
                <ToggleRow
                  icon={BarChart3} label="Portfolio Notifications"
                  description="Weekly scores, Power Play reminders"
                  checked={inAppPrefs?.portfolio_notifications ?? true}
                  onChange={(v) => updateInApp('portfolio_notifications', v)}
                  color="blue" testId="toggle-portfolio"
                />
                <ToggleRow
                  icon={Users} label="Social Notifications"
                  description="New followers, mentions"
                  checked={inAppPrefs?.social_notifications ?? true}
                  onChange={(v) => updateInApp('social_notifications', v)}
                  color="emerald" testId="toggle-social"
                />
                <ToggleRow
                  icon={Megaphone} label="System Notifications"
                  description="Platform announcements, feature updates"
                  checked={inAppPrefs?.system_notifications ?? true}
                  onChange={(v) => updateInApp('system_notifications', v)}
                  color="slate" testId="toggle-system"
                />
                <ToggleRow
                  icon={Crown} label="Pro Notifications"
                  description="Subscription renewals, expiry warnings"
                  checked={inAppPrefs?.pro_notifications ?? true}
                  onChange={(v) => updateInApp('pro_notifications', v)}
                  color="yellow" testId="toggle-pro"
                />

                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 mt-4">Specific Alerts</p>
                <ToggleRow
                  icon={TrendingUp} label="Rank Changes"
                  description="Get notified when your league rank changes"
                  checked={inAppPrefs?.rank_change_notifications ?? true}
                  onChange={(v) => updateInApp('rank_change_notifications', v)}
                  testId="toggle-rank-change"
                />
                <ToggleRow
                  icon={BarChart3} label="Weekly Scores"
                  description="Notification when weekly scoring is complete"
                  checked={inAppPrefs?.weekly_score_notifications ?? true}
                  onChange={(v) => updateInApp('weekly_score_notifications', v)}
                  testId="toggle-weekly-score"
                />
                <ToggleRow
                  icon={Clock} label="Season Reminders"
                  description="Alerts when a season is ending soon"
                  checked={inAppPrefs?.season_reminders ?? true}
                  onChange={(v) => updateInApp('season_reminders', v)}
                  testId="toggle-season-reminders"
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Email Notifications */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <Mail className="h-5 w-5 text-emerald-400" /> Email Notifications
                </CardTitle>
                <CardDescription className="mt-1">Manage email reminders sent to {user?.email}</CardDescription>
              </div>
              <Switch
                checked={inAppPrefs?.email_enabled ?? true}
                onCheckedChange={(v) => updateInApp('email_enabled', v)}
                data-testid="toggle-email-master"
              />
            </div>
          </CardHeader>
          {inAppPrefs?.email_enabled && (
            <CardContent className="pt-0">
              <div className="mb-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Frequency</p>
                <div className="flex gap-2">
                  {['instant', 'daily', 'weekly', 'never'].map((freq) => (
                    <Button
                      key={freq}
                      size="sm"
                      variant={inAppPrefs?.email_frequency === freq ? 'default' : 'outline'}
                      className={inAppPrefs?.email_frequency === freq ? 'bg-emerald-600' : 'border-slate-700 text-slate-400'}
                      onClick={() => updateEmailFrequency(freq)}
                      data-testid={`freq-${freq}`}
                    >
                      {freq.charAt(0).toUpperCase() + freq.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Email Types</p>
              <ToggleRow
                icon={Mail} label="Email Reminders"
                description="General email notifications and updates"
                checked={emailPrefs?.email_reminders ?? true}
                onChange={(v) => updateEmail('email_reminders', v)}
                testId="toggle-email-reminders"
              />
              <ToggleRow
                icon={Sparkles} label="Chip Reminders"
                description="Reminders to claim your weekly chips"
                checked={emailPrefs?.chip_reminders ?? true}
                onChange={(v) => updateEmail('chip_reminders', v)}
                testId="toggle-chip-reminders"
              />
              <ToggleRow
                icon={Zap} label="Trading Reminders"
                description="Weekly nudges to review and trade your portfolio"
                checked={emailPrefs?.trading_reminders ?? true}
                onChange={(v) => updateEmail('trading_reminders', v)}
                testId="toggle-trading-reminders"
              />
            </CardContent>
          )}
        </Card>

        {/* Quiet Hours */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <Moon className="h-5 w-5 text-indigo-400" /> Quiet Hours
                </CardTitle>
                <CardDescription className="mt-1">Pause non-urgent notifications during these hours (UTC)</CardDescription>
              </div>
              <Switch
                checked={inAppPrefs?.quiet_hours_enabled ?? false}
                onCheckedChange={(v) => updateQuietHours('quiet_hours_enabled', v)}
                data-testid="toggle-quiet-hours"
              />
            </div>
          </CardHeader>
          {inAppPrefs?.quiet_hours_enabled && (
            <CardContent className="pt-0">
              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-xs text-slate-400">Start</Label>
                  <select
                    value={inAppPrefs?.quiet_hours_start ?? 22}
                    onChange={(e) => updateQuietHours('quiet_hours_start', parseInt(e.target.value))}
                    className="block mt-1 bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm"
                    data-testid="quiet-start"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                    ))}
                  </select>
                </div>
                <span className="text-slate-500 mt-5">to</span>
                <div>
                  <Label className="text-xs text-slate-400">End</Label>
                  <select
                    value={inAppPrefs?.quiet_hours_end ?? 8}
                    onChange={(e) => updateQuietHours('quiet_hours_end', parseInt(e.target.value))}
                    className="block mt-1 bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm"
                    data-testid="quiet-end"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Urgent and high-priority notifications will still be delivered during quiet hours.
              </p>
            </CardContent>
          )}
        </Card>

        {/* Portfolio Privacy (Pro Only) */}
        <Card className="bg-slate-900/50 border-slate-800" data-testid="privacy-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <Shield className="h-5 w-5 text-violet-400" /> Portfolio Privacy
                </CardTitle>
                <CardDescription className="mt-1">Control who can see your portfolio holdings</CardDescription>
              </div>
              {user?.is_pro && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                  <Crown className="h-3 w-3 mr-1" /> Pro
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {portfolioPublic ? <Eye className="h-4 w-4 text-emerald-400 flex-shrink-0" /> : <EyeOff className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                <div className="min-w-0">
                  <Label className="text-white text-sm font-medium">
                    {portfolioPublic ? 'Portfolio is Public' : 'Portfolio is Private'}
                  </Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {portfolioPublic
                      ? 'Other players can see your holdings when they visit your profile'
                      : 'Your holdings are hidden — only stats and cosmetics are visible'
                    }
                  </p>
                </div>
              </div>
              {user?.is_pro ? (
                <Switch
                  checked={portfolioPublic}
                  onCheckedChange={togglePortfolioPrivacy}
                  data-testid="toggle-portfolio-privacy"
                />
              ) : (
                <Badge variant="outline" className="text-slate-500 border-slate-700 text-xs whitespace-nowrap">
                  <Crown className="h-3 w-3 mr-1" /> Pro Only
                </Badge>
              )}
            </div>
            {!user?.is_pro && (
              <p className="text-xs text-slate-600 mt-1 border-t border-slate-800 pt-2">
                Upgrade to Pro to hide your portfolio from competitors while keeping your profile visible.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center">
                  <span className="text-emerald-400 font-bold text-lg">{user?.username?.[0]?.toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-white font-medium">{user?.username}</p>
                  <p className="text-sm text-slate-400">{user?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {user?.is_pro && (
                  <Badge className="bg-amber-500/20 text-amber-400">
                    <Crown className="h-3 w-3 mr-1" /> Pro
                  </Badge>
                )}
                <Badge variant="outline" className="text-slate-400 border-slate-700">
                  {user?.role || 'Player'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
